import { Clock3Icon } from "lucide-react";
import { Link } from "react-router";

import { EarningsCardAmounts } from "./EarningsCardAmounts";
import { EarningsCardRailSection } from "./EarningsCardRailSection";

import { Column } from "@/components/Column";
import { ErrorText } from "@/components/ErrorText";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Avatar } from "@/components/quick-access/Avatar";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

import { useGetStafferEarnings } from "@/hooks/queries/useGetStafferEarnings";
import { dates } from "@/lib/dates";
import { earningsProgress } from "@/lib/earningsProgress";
import { formatting } from "@/lib/formatting";
import { getCandidacyLink } from "@/lib/notificationLinks";
import { cn } from "@/lib/utils";
import { CallDirection, EarningsOffer, LastPing, PingType } from "@/types";

// "Email sent today at 9:42 AM" / "Call yesterday at 3:18 PM" / "Text sent Jul 14 at 11:06 AM"
const formatLastCommunication = (lastPing: LastPing): string => {
  const isIncoming = lastPing.direction === CallDirection.INCOMING;

  let typeLabel: string;
  if (lastPing.type === PingType.CALL) {
    typeLabel = isIncoming ? "Call received" : "Call";
  } else if (lastPing.type === PingType.EMAIL) {
    typeLabel = isIncoming ? "Email received" : "Email sent";
  } else {
    typeLabel = isIncoming ? "Text received" : "Text sent";
  }

  const at = new Date(lastPing.at);
  const now = new Date();

  let dayLabel: string;
  if (dates.localDayKey(at) === dates.localDayKey(now)) {
    dayLabel = "today";
  } else if (
    dates.localDayKey(at) === dates.localDayKey(dates.addLocalDays(now, -1))
  ) {
    dayLabel = "yesterday";
  } else {
    dayLabel = at.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    });
  }

  return `${typeLabel} ${dayLabel} at ${dates.formatTime(at)}`;
};

const getCandidateInitials = (name: string): string => {
  const nameParts = name.trim().split(/\s+/);

  return formatting.initials(nameParts[0], nameParts.at(-1));
};

export const EarningsOutstandingOffersSection = ({
  selectedUserId,
}: {
  selectedUserId?: number;
}) => {
  /* Database CRUD Operations */
  // Read
  const {
    data: earnings,
    isPending,
    isError,
  } = useGetStafferEarnings({ userId: selectedUserId });

  /* Helpers */
  // Each offer's commission is its revenue stacked on top of the current YTD
  // basis, blended across any tier boundary it crosses.
  const getOfferCommission = (offer: EarningsOffer): number => {
    if (!earnings) return 0;

    return earningsProgress.commissionBetween(
      earnings.totalRevenueYtd,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      earnings.totalRevenueYtd + (offer.revenueEarned || 0),
      earnings.commissionTiers,
    );
  };

  /* Render */
  const renderSkeleton = () => (
    <Column className="gap-3">
      <Skeleton className="h-3 w-44" />
      <Row className="gap-4">
        <Skeleton className="h-44 w-80 rounded-lg" />
        <Skeleton className="h-44 w-80 rounded-lg" />
      </Row>
    </Column>
  );

  const render = () => {
    if (isPending) {
      return renderSkeleton();
    }

    if (isError) {
      return <ErrorText errorMessage="Failed to load outstanding offers." />;
    }

    const offers = earnings.outstandingOffers;

    // No outstanding offers hides the whole section
    if (offers.length === 0) {
      return null;
    }

    const commissionTotal = offers.reduce(
      (total, offer) => total + getOfferCommission(offer),
      0,
    );

    return (
      <EarningsCardRailSection
        cardCount={offers.length}
        commissionTotal={commissionTotal}
        label="Outstanding offers"
      >
        {offers.map((offer) => (
          <OutstandingOfferCard
            key={offer.id}
            commissionDollars={getOfferCommission(offer)}
            offer={offer}
          />
        ))}
      </EarningsCardRailSection>
    );
  };

  return render();
};

const OutstandingOfferCard = ({
  commissionDollars,
  offer,
}: {
  commissionDollars: number;
  offer: EarningsOffer;
}) => {
  /* Render */
  const render = () => (
    <Card className="w-80 flex-none p-5 shadow-none">
      <Column className="h-full gap-4">
        <Row className="min-w-0 items-start gap-2">
          <Avatar initials={getCandidateInitials(offer.candidateName)} />
          <Column className="min-w-0 flex-1 gap-1">
            <Link
              className="w-fit max-w-full"
              to={getCandidacyLink({
                candidacyId: offer.candidacyId,
                companyId: offer.companyId,
                jobId: offer.jobId,
              })}
            >
              <Text className="truncate text-sm font-medium text-slate-900 hover:underline">
                {offer.candidateName}
              </Text>
            </Link>
            <Text className="truncate text-xs font-normal text-slate-500">
              {offer.jobTitle} · {offer.companyName}
            </Text>
          </Column>
        </Row>
        {/* No-comms offers render the pill invisibly so every card lines up */}
        <Row
          className={cn(
            "w-fit min-w-0 max-w-full items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1.5",
            !offer.lastCommunication && "invisible",
          )}
        >
          <Clock3Icon className="h-3 w-3 shrink-0 text-slate-400" />
          <Text className="truncate text-xs font-normal text-slate-900">
            {offer.lastCommunication
              ? formatLastCommunication(offer.lastCommunication)
              : "No communication yet"}
          </Text>
        </Row>
        <EarningsCardAmounts
          commissionDollars={commissionDollars}
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          revenueDollars={offer.revenueEarned || 0}
        />
      </Column>
    </Card>
  );

  return render();
};
